import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    Button,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Send as SendIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    AccountBalance as VaultIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    AdminPanelSettings as AdminIcon,
    Engineering as EngineeringIcon,
    Computer as ComputerIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Global } from '@emotion/react';
import { 
    AuditLogFilters, 
    AuditLogEvent, 
    useAuditLogs,
    getDomainIcon,
    getStatusColor,
    formatEventDate,
    truncateText,
    extractCorrelationIds
} from '../../services/auditLogsService';

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

/**
 * Reusable DetailRow component for consistent data display
 */
const DetailRow: React.FC<{ label: string; value: string | number | null | undefined }> = ({ label, value }) => (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        <strong>{label}:</strong> {value || 'N/A'}
    </Typography>
);

/**
 * Email-specific details component
 */
const EmailDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        {/* Email-Specific Information */}
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                📧 Email Information
            </Typography>
            <DetailRow label="Email Type" value={event.EmailType} />
            <DetailRow label="To Address" value={event.ToAddress} />
            <DetailRow label="Subject" value={event.Subject} />
            <DetailRow label="Message ID" value={event.MessageId} />
            <DetailRow label="Provider" value={event.Provider} />
        </Grid>
        
        {/* Business Context */}
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🏢 Business Context
            </Typography>
            <DetailRow label="Consent Header ID" value={event.ConsentHeaderId} />
            <DetailRow label="Request ID" value={event.RequestId} />
            <DetailRow label="Template Key" value={event.TemplateKey} />
            <DetailRow label="Delivered Date" value={event.DeliveredDate ? formatEventDate(event.DeliveredDate) : 'Not delivered'} />
        </Grid>
        
        {/* Template Context from Additional Data */}
        {event.AdditionalData && (
            <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                    🎨 Template Context
                </Typography>
                <DetailRow label="Client Subdomain" value={event.AdditionalData.clientSubdomain} />
                <DetailRow label="Language Code" value={event.AdditionalData.languageCode} />
                <DetailRow label="Double Opt-in" value={event.AdditionalData.isDoubleOptIn ? 'Yes' : 'No'} />
                {event.AdditionalData.templateData && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Template Data Used:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {event.AdditionalData.templateData.map((item: string, index: number) => (
                                <Chip key={index} label={item} size="small" />
                            ))}
                        </Box>
                    </Box>
                )}
            </Grid>
        )}
    </Grid>
);

/**
 * Vault Access details component
 */
const VaultAccessDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔐 Vault Access Information
            </Typography>
            <DetailRow label="Vault ID" value={event.VaultId} />
            <DetailRow label="Access Type" value={event.AccessType} />
            <DetailRow label="Access Reason" value={event.AccessReason} />
            <DetailRow label="User ID" value={event.UserId} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔒 Security Context
            </Typography>
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
            <DetailRow label="Access Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * DP Vault Access details component
 */
const DPVaultAccessDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                👤 Data Principal Vault Access
            </Typography>
            <DetailRow label="Vault ID" value={event.VaultId} />
            <DetailRow label="Data Principal ID" value={event.DPId} />
            <DetailRow label="Access Type" value={event.AccessType} />
            <DetailRow label="Access Reason" value={event.AccessReason} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔒 Access Context
            </Typography>
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
            <DetailRow label="Access Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * DP Portal details component
 */
const DPPortalDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🌐 Portal Access Information
            </Typography>
            <DetailRow label="Data Principal ID" value={event.DPId} />
            <DetailRow label="Action" value={event.ActionDescription} />
            <DetailRow label="Resource" value={event.ResourceDescription} />
            <DetailRow label="Access Reason" value={event.AccessReason} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔍 Technical Details
            </Typography>
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
            <DetailRow label="Event Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * Consent Retention details component
 */
const ConsentRetentionDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                📋 Consent Retention Information
            </Typography>
            <DetailRow label="Consent Header ID" value={event.ConsentHeaderId} />
            <DetailRow label="Request ID" value={event.RequestId} />
            <DetailRow label="Action" value={event.ActionDescription} />
            <DetailRow label="Resource" value={event.ResourceDescription} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                ⏰ Retention Context
            </Typography>
            <DetailRow label="Data Principal ID" value={event.DPId} />
            <DetailRow label="Event Date" value={formatEventDate(event.EventDate)} />
            <DetailRow label="Status" value={event.Status} />
            <DetailRow label="Success" value={event.Success ? 'Yes' : 'No'} />
        </Grid>
    </Grid>
);

/**
 * TPP Deletion details component
 */
const TPPDeletionDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🏢 TPP Deletion Information
            </Typography>
            <DetailRow label="Request ID" value={event.RequestId} />
            <DetailRow label="Action" value={event.ActionDescription} />
            <DetailRow label="Resource" value={event.ResourceDescription} />
            <DetailRow label="Status" value={event.Status} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔍 Technical Details
            </Typography>
            <DetailRow label="User/DP ID" value={event.DPId || event.UserId} />
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Event Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * Privigo Admin details component
 */
const PrivigoAdminDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                ⚙️ Admin Action Information
            </Typography>
            <DetailRow label="Admin User ID" value={event.UserId} />
            <DetailRow label="Action" value={event.ActionDescription} />
            <DetailRow label="Resource" value={event.ResourceDescription} />
            <DetailRow label="Access Reason" value={event.AccessReason} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔒 Security Context
            </Typography>
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
            <DetailRow label="Event Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * Privigo Ops details component
 */
const PrivigoOpsDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔧 Operations Information
            </Typography>
            <DetailRow label="Operation" value={event.ActionDescription} />
            <DetailRow label="Resource" value={event.ResourceDescription} />
            <DetailRow label="Status" value={event.Status} />
            <DetailRow label="Triggered By" value={event.UserId} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                📊 System Context
            </Typography>
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
            <DetailRow label="Event Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * System details component
 */
const SystemDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                💻 System Event Information
            </Typography>
            <DetailRow label="Event Type" value={event.ActionDescription} />
            <DetailRow label="Component" value={event.ResourceDescription} />
            <DetailRow label="Status" value={event.Status} />
            <DetailRow label="Success" value={event.Success ? 'Yes' : 'No'} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔍 Technical Details
            </Typography>
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
            <DetailRow label="Event Date" value={formatEventDate(event.EventDate)} />
        </Grid>
    </Grid>
);

/**
 * Generic details component for unknown domains
 */
const GenericDetails: React.FC<{ event: AuditLogEvent }> = ({ event }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                📊 Event Information
            </Typography>
            <DetailRow label="Event ID" value={event.EventId} />
            <DetailRow label="Domain" value={event.DomainDescription} />
            <DetailRow label="Action" value={event.ActionDescription} />
            <DetailRow label="Resource" value={event.ResourceDescription} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary">
                🔍 Technical Details
            </Typography>
            <DetailRow label="User/DP ID" value={event.DPId || event.UserId} />
            <DetailRow label="IP Address" value={event.IPAddress} />
            <DetailRow label="User Agent" value={event.UserAgent} />
            <DetailRow label="Session ID" value={event.SessionId} />
        </Grid>
    </Grid>
);

// ============================================================================
// AUDIT LOGS COMPONENT
// ============================================================================

const AuditLogs: React.FC = () => {
    const theme = useTheme();
    
    // State management
    const [filters, setFilters] = useState<AuditLogFilters>({
        page: 1,
        pageSize: 20
    });
    const [tempFilters, setTempFilters] = useState<AuditLogFilters>(filters);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [selectedEvent, setSelectedEvent] = useState<AuditLogEvent | null>(null);
    const [detailsAnchorEl, setDetailsAnchorEl] = useState<null | HTMLElement>(null);
    
    // Fetch audit logs
    const { data: auditData, loading, error, refetch } = useAuditLogs(filters);

    // Handle filter changes
    const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
        setTempFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        setFilters({ ...tempFilters, page: 1 });
    };

    const clearFilters = () => {
        const clearedFilters = { page: 1, pageSize: 20 };
        setTempFilters(clearedFilters);
        setFilters(clearedFilters);
    };

    // Remove individual filter and immediately apply
    const removeFilter = (key: keyof AuditLogFilters) => {
        const newFilters = { ...filters, [key]: undefined, page: 1 };
        setFilters(newFilters);
        setTempFilters(prev => ({ ...prev, [key]: undefined }));
    };

    // Clear all filters and immediately apply
    const clearAllFilters = () => {
        const clearedFilters = { page: 1, pageSize: 20 };
        setFilters(clearedFilters);
        setTempFilters(clearedFilters);
    };

    const hasActiveFilters = () => {
        return Object.entries(filters).some(([key, value]) => 
            key !== 'page' && key !== 'pageSize' && value !== undefined && value !== ''
        );
    };

    // Handle pagination
    const handleChangePage = (event: unknown, newPage: number) => {
        setFilters(prev => ({ ...prev, page: newPage + 1 }));
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => ({ 
            ...prev, 
            pageSize: parseInt(event.target.value, 10),
            page: 1
        }));
    };

    // Handle row expansion
    const toggleRowExpansion = (eventId: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(eventId)) {
                newSet.delete(eventId);
            } else {
                newSet.add(eventId);
            }
            return newSet;
        });
    };

    // Handle event details
    const handleViewDetails = (event: React.MouseEvent<HTMLElement>, auditEvent: AuditLogEvent) => {
        setSelectedEvent(auditEvent);
        setDetailsAnchorEl(event.currentTarget);
    };

    // Get domain icon
    const getDomainIconComponent = (domain: string) => {
        const iconMap: { [key: string]: React.ReactElement } = {
            'DP_PORTAL': <PersonIcon />,
            'EMAIL': <EmailIcon />,
            'VAULT_ACCESS': <VaultIcon />,
            'DP_VAULT_ACCESS': <VaultIcon />,
            'TPP_DELETION': <BusinessIcon />,
            'CONSENT_RETENTION': <ScheduleIcon />,
            'KAVACH_ADMIN': <AdminIcon />,
            'KAVACH_OPS': <EngineeringIcon />,
            'SYSTEM': <ComputerIcon />,
            'event-propagation': <SendIcon />,
            'RIGHTS_REQUEST': <AssignmentIcon />
        };
        return iconMap[domain] || <ComputerIcon />;
    };

    // Get domain-specific details component
    const getDomainSpecificComponent = (event: AuditLogEvent) => {
        switch (event.Domain) {
            case 'EMAIL':
                return <EmailDetails event={event} />;
            case 'VAULT_ACCESS':
                return <VaultAccessDetails event={event} />;
            case 'DP_VAULT_ACCESS':
                return <DPVaultAccessDetails event={event} />;
            case 'DP_PORTAL':
                return <DPPortalDetails event={event} />;
            case 'CONSENT_RETENTION':
                return <ConsentRetentionDetails event={event} />;
            case 'TPP_DELETION':
                return <TPPDeletionDetails event={event} />;
            case 'KAVACH_ADMIN':
                return <PrivigoAdminDetails event={event} />;
            case 'KAVACH_OPS':
                return <PrivigoOpsDetails event={event} />;
            case 'SYSTEM':
                return <SystemDetails event={event} />;
            default:
                return <GenericDetails event={event} />;
        }
    };

    // Render expanded row content
    const renderExpandedContent = (auditEvent: AuditLogEvent) => {
        const correlationIds = extractCorrelationIds(auditEvent.AdditionalData);
        
        return (
            <TableRow>
                <TableCell colSpan={8} sx={{ py: 2, backgroundColor: theme.palette.background.default }}>
                    <Box sx={{ pl: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Additional Details
                        </Typography>
                        
                        {/* Domain-specific details */}
                        {getDomainSpecificComponent(auditEvent)}
                        
                        {/* Common elements */}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {/* Correlation IDs */}
                            {correlationIds.length > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom color="primary">
                                        🔗 Correlation IDs
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {correlationIds.map((id, index) => (
                                            <Chip 
                                                key={index}
                                                label={id} 
                                                size="small" 
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            )}
                            
                            {/* Error Message */}
                            {auditEvent.ErrorMessage && (
                                <Grid item xs={12}>
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        <strong>Error:</strong> {auditEvent.ErrorMessage}
                                    </Alert>
                                </Grid>
                            )}
                            
                            {/* Raw Additional Data for debugging */}
                            {auditEvent.AdditionalData && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom color="primary">
                                        📋 Raw Additional Data
                                    </Typography>
                                    <Paper sx={{ 
                                        p: 1, 
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? theme.palette.grey[800] 
                                            : theme.palette.grey[100]
                                    }}>
                                        <pre style={{ 
                                            fontSize: '0.75rem', 
                                            margin: 0, 
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            color: theme.palette.text.primary
                                        }}>
                                            {JSON.stringify(auditEvent.AdditionalData, null, 2)}
                                        </pre>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* CSS Animation for Refresh Icon */}
            <Global styles={`
                @keyframes spin {
                    100% { transform: rotate(360deg); }
                }
            `} />
            
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Audit Logs
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        onClick={() => refetch()}
                        sx={{ color: theme.palette.text.primary }}
                        disabled={loading}
                    >
                        <RefreshIcon
                            sx={{
                                transition: 'transform 0.3s',
                                animation: loading ? 'spin 1s linear infinite' : undefined
                            }}
                        />
                    </IconButton>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                            // TODO: Implement export functionality
                            console.log('Export functionality to be implemented');
                        }}
                    >
                        Export
                    </Button>
                </Box>
            </Box>

            {/* Filter Bar */}
            <Paper sx={{ 
                p: 1.5, 
                mb: 2,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Search events..."
                        value={tempFilters.search || ''}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{ minWidth: 180 }}
                    />
                    
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Domain</InputLabel>
                        <Select
                            value={tempFilters.domain || 'ALL'}
                            label="Domain"
                            onChange={(e) => handleFilterChange('domain', e.target.value)}
                        >
                            <MenuItem value="ALL">All Domains</MenuItem>
                            <MenuItem value="RIGHTS_REQUEST">Rights Request</MenuItem>
                            <MenuItem value="DP_PORTAL">DP Portal</MenuItem>
                            <MenuItem value="EMAIL">Email System</MenuItem>
                            <MenuItem value="VAULT_ACCESS">Vault Access</MenuItem>
                            <MenuItem value="DP_VAULT_ACCESS">DP Vault Access</MenuItem>
                            <MenuItem value="TPP_DELETION">TPP Deletion</MenuItem>
                            <MenuItem value="CONSENT_RETENTION">Consent Retention</MenuItem>
                            <MenuItem value="KAVACH_ADMIN">Privigo Admin</MenuItem>
                            <MenuItem value="KAVACH_OPS">Privigo Ops</MenuItem>
                            <MenuItem value="SYSTEM">System Events</MenuItem>
                            <MenuItem value="event-propagation">Event Propagation</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={tempFilters.status || 'ALL'}
                            label="Status"
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <MenuItem value="ALL">All Status</MenuItem>
                            <MenuItem value="SUCCESS">Success</MenuItem>
                            <MenuItem value="FAILURE">Failure</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        value={tempFilters.startDate || ''}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ 
                            minWidth: 140,
                            '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                                cursor: 'pointer'
                            },
                            '& input[type="date"]::-webkit-inner-spin-button': {
                                display: 'none'
                            },
                            '& input[type="date"]::-webkit-clear-button': {
                                display: 'none'
                            }
                        }}
                    />
                    
                    <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        value={tempFilters.endDate || ''}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ 
                            minWidth: 140,
                            '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                                cursor: 'pointer'
                            },
                            '& input[type="date"]::-webkit-inner-spin-button': {
                                display: 'none'
                            },
                            '& input[type="date"]::-webkit-clear-button': {
                                display: 'none'
                            }
                        }}
                    />
                    
                    <Button
                        variant="contained"
                        size="small"
                        onClick={applyFilters}
                        disabled={loading}
                    >
                        Apply
                    </Button>
                    
                    {hasActiveFilters() && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ClearIcon />}
                            onClick={clearAllFilters}
                        >
                            Clear All
                        </Button>
                    )}
                </Box>
                
                {/* Active Filters Display */}
                {hasActiveFilters() && (
                    <Box sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: `1px solid ${theme.palette.divider}` 
                    }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Active Filters:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {filters.domain && filters.domain !== 'ALL' && (
                                <Chip 
                                    label={`Domain: ${filters.domain}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onDelete={() => removeFilter('domain')}
                                />
                            )}
                            {filters.status && filters.status !== 'ALL' && (
                                <Chip 
                                    label={`Status: ${filters.status}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onDelete={() => removeFilter('status')}
                                />
                            )}
                            {filters.startDate && (
                                <Chip 
                                    label={`From: ${new Date(filters.startDate).toLocaleDateString()}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onDelete={() => removeFilter('startDate')}
                                />
                            )}
                            {filters.endDate && (
                                <Chip 
                                    label={`To: ${new Date(filters.endDate).toLocaleDateString()}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onDelete={() => removeFilter('endDate')}
                                />
                            )}
                            {filters.search && (
                                <Chip 
                                    label={`Search: ${filters.search}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onDelete={() => removeFilter('search')}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Data Display */}
            <Paper sx={{ 
                p: 3,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`
            }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Error loading audit logs: {error.message}
                    </Alert>
                )}
                
                {auditData && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Audit Events ({auditData.totalCount} total)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Page {auditData.currentPage} of {auditData.totalPages} • 
                                Showing {auditData.events.length} records
                            </Typography>
                        </Box>
                        
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Timestamp</TableCell>
                                        <TableCell>Domain</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell>Resource</TableCell>
                                        <TableCell>User/DP</TableCell>
                                        <TableCell>IP Address</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {auditData.events.map((event) => (
                                        <React.Fragment key={event.EventId}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatEventDate(event.EventDate)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {getDomainIconComponent(event.Domain)}
                                                        <Typography variant="body2">
                                                            {event.DomainDescription}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {event.ActionDescription}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {truncateText(event.ResourceDescription, 30)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {event.DPId || event.UserId || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {event.IPAddress || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={event.Success ? <CheckCircleIcon /> : <ErrorIcon />}
                                                        label={event.Status}
                                                        size="small"
                                                        color={event.Success ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => handleViewDetails(e, event)}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Expand Row">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleRowExpansion(event.EventId)}
                                                            >
                                                                {expandedRows.has(event.EventId) ? 
                                                                    <ExpandLessIcon /> : 
                                                                    <ExpandMoreIcon />
                                                                }
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            {expandedRows.has(event.EventId) && renderExpandedContent(event)}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <TablePagination
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            component="div"
                            count={auditData.totalCount}
                            rowsPerPage={auditData.pageSize}
                            page={auditData.currentPage - 1}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiTablePagination-selectIcon': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        />
                    </>
                )}
            </Paper>

            {/* Event Details Menu */}
            <Menu
                anchorEl={detailsAnchorEl}
                open={Boolean(detailsAnchorEl)}
                onClose={() => setDetailsAnchorEl(null)}
                PaperProps={{
                    sx: { 
                        minWidth: 400, 
                        maxWidth: 600,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                {selectedEvent && (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Event Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Event ID:</strong> {selectedEvent.EventId}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Timestamp:</strong> {formatEventDate(selectedEvent.EventDate)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Domain:</strong> {selectedEvent.DomainDescription}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Action:</strong> {selectedEvent.ActionDescription}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Resource:</strong> {selectedEvent.ResourceDescription}
                                </Typography>
                            </Grid>
                            {selectedEvent.ErrorMessage && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        <strong>Error:</strong> {selectedEvent.ErrorMessage}
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </Menu>
        </Box>
    );
};

export default AuditLogs;
